import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const LogoCarousel = () => {
  // Logos configuration with actual available logos
  const logos = [
    {
      name: 'Amazon',
      imgSrc: '/partner-logos/Amazon-Logo.webp',
      width: 180, // Increased by 50% from 120
      height: 72, // Increased by 50% from 48
    },
    {
      name: 'Deloitte',
      imgSrc: '/partner-logos/Deloitte-Logo.webp',
      width: 165, // Increased by 50% from 110
      height: 72, // Increased by 50% from 48
    },
    {
      name: 'Spring Fertility',
      imgSrc: '/partner-logos/Spring_Fertility_Blue_Logo.webp',
      width: 195, // Increased by 50% from 130
      height: 72, // Increased by 50% from 48
    },
    {
      name: 'Shabbat Club',
      imgSrc: '/partner-logos/ShabbatClubLogo.webp',
      width: 165,
      height: 72,
    },
    {
      name: 'Moxie Communications Group',
      imgSrc: '/partner-logos/moxie_communications_group_logo.jpeg',
      width: 180,
      height: 72,
    },
    
    /* 
    // ADDING MORE LOGOS: 
    // To add more logos, copy and paste this template and add your new logo:
    {
      name: 'New Partner Company',
      imgSrc: '/partner-logos/new-partner-logo.webp',
      width: 180, // Adjust width based on logo proportions (150-200px works well)
      height: 72,  // Keep height consistent for alignment
    },
    
    // INSTRUCTIONS:
    // 1. Add new logo files to the public/partner-logos/ directory
    // 2. WebP format is recommended for better performance
    // 3. Maintain similar dimensions to existing logos for consistent appearance
    // 4. Increase spacing in the carousel if adding many logos (adjust space-x values in className below)
    */
  ];

  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);

  // Calculate the total width of a single set of logos including their spacing
  useEffect(() => {
    if (containerRef.current) {
      const firstSetWidth = Array.from(containerRef.current.children)
        .slice(0, logos.length)
        .reduce((total, child) => {
          const style = window.getComputedStyle(child);
          const marginRight = parseInt(style.marginRight || '0');
          return total + child.offsetWidth + marginRight;
        }, 0);
      
      setContainerWidth(firstSetWidth);
    }
  }, [logos.length]);

  return (
    <div className="w-full overflow-hidden py-2 sm:py-3">
      <p className="text-center text-xs sm:text-sm tracking-[0.14em] uppercase text-stone-500 mb-3 sm:mb-4 font-sans font-medium">
        Trusted by leading companies
      </p>
      
      {/* Logo carousel - animates from right to left */}
      <div className="relative w-full overflow-hidden">
        {/* Logo container with animation */}
        <motion.div 
          ref={containerRef}
          className="flex items-center space-x-14 sm:space-x-18 md:space-x-22 lg:space-x-28 py-1 sm:py-2 px-4"
          animate={{ 
            x: containerWidth > 0 ? [-0, -containerWidth] : 0 
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: 20,
              ease: "linear",
              repeatDelay: 0
            }
          }}
          style={{
            width: containerWidth > 0 ? `${containerWidth * 2}px` : "auto"
          }}
        >
          {/* First set of logos */}
          {logos.map((logo, index) => (
            <div 
              key={`logo-1-${index}`}
              className="flex items-center justify-center opacity-50 hover:opacity-80 transition-all duration-300 grayscale"
              style={{ 
                minWidth: `${Math.round(logo.width * 0.75)}px`,
                height: `${Math.round(logo.height * 0.75)}px`
              }}
            >
              <img 
                src={logo.imgSrc} 
                alt={`${logo.name} logo - LetsHost partner`}
                className="object-contain max-h-full max-w-full" 
                loading={index < 4 ? "eager" : "lazy"}
                width={Math.round(logo.width * 0.75)}
                height={Math.round(logo.height * 0.75)}
              />
            </div>
          ))}
          
          {/* Duplicate set for seamless looping */}
          {logos.map((logo, index) => (
            <div 
              key={`logo-2-${index}`}
              className="flex items-center justify-center opacity-50 hover:opacity-80 transition-all duration-300 grayscale"
              style={{ 
                minWidth: `${Math.round(logo.width * 0.75)}px`,
                height: `${Math.round(logo.height * 0.75)}px`
              }}
            >
              <img 
                src={logo.imgSrc} 
                alt={`${logo.name} logo - LetsHost partner`}
                className="object-contain max-h-full max-w-full" 
                loading="lazy"
                width={Math.round(logo.width * 0.75)}
                height={Math.round(logo.height * 0.75)}
              />
            </div>
          ))}
        </motion.div>
        
        {/* Subtle fade on edges */}
        <div className="absolute pointer-events-none top-0 left-0 h-full w-10 sm:w-14 md:w-20 bg-gradient-to-r from-[#F5F0EB] to-transparent z-10"></div>
        <div className="absolute pointer-events-none top-0 right-0 h-full w-10 sm:w-14 md:w-20 bg-gradient-to-l from-[#F5F0EB] to-transparent z-10"></div>
      </div>
    </div>
  );
};

export default LogoCarousel; 